type DownloadFileParams = {
  ext: string
  filename?: string
  targetWindow?: Window | null
}

type DownloadFile = (base64FileData: string, fileParams: DownloadFileParams) => void

const RECEIPT_PREVIEW_HTML =
  '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Чек</title></head><body style="margin:0;min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:-apple-system,sans-serif;color:#1d2023">Загрузка чека…</body></html>'

const MIME_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf',
}

const isIosSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false

  const { userAgent, platform, maxTouchPoints } = navigator
  const isIos = /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1)

  if (!isIos) return false

  return !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/i.test(userAgent)
}

const normalizeBase64 = (value: string) => value.replace(/^data:[^;]+;base64,/i, '').replace(/\s/g, '')

const base64ToBlob = (base64FileData: string, mimeType: string) => {
  const binary = atob(normalizeBase64(base64FileData))
  const bytes = new Uint8Array(binary.length)

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }

  return new Blob([bytes], { type: mimeType })
}

const downloadBlob = (blob: Blob, fileName: string) => {
  const source = URL.createObjectURL(blob)
  const downloadLink = document.createElement('a')

  downloadLink.href = source
  downloadLink.download = fileName
  downloadLink.rel = 'noopener'
  document.body.appendChild(downloadLink)
  downloadLink.click()
  downloadLink.remove()

  setTimeout(() => URL.revokeObjectURL(source), 40_000)
}

const showBlobInWindow = (previewWindow: Window, blob: Blob) => {
  const source = URL.createObjectURL(blob)

  previewWindow.location.href = source
  setTimeout(() => URL.revokeObjectURL(source), 120_000)
}

export const openPdfFromBase64 = (data: string) => {
  const pdfWindow = window.open('')

  pdfWindow?.document.write(
    `<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(data)}'></iframe>`
  )
}

export const calculateBase64FileSize = (file: string) => Buffer.from(file || '').length // bytes

export const openIosFilePreview = (): Window | null => {
  if (!isIosSafari()) return null

  const previewWindow = window.open('about:blank', '_blank')

  if (!previewWindow) return null

  try {
    previewWindow.document.open()
    previewWindow.document.write(RECEIPT_PREVIEW_HTML)
    previewWindow.document.close()
  } catch {
    // Вкладка уже открыта в жесте пользователя, PDF подставится после ответа сервера.
  }

  return previewWindow
}

export const closeIosFilePreview = (previewWindow?: Window | null) => {
  if (previewWindow && !previewWindow.closed) {
    previewWindow.close()
  }
}

export const downloadBase64AsFile: DownloadFile = (
  base64FileData,
  { ext, filename = base64FileData.substring(0, Math.min(base64FileData.length, 8)).toLocaleLowerCase(), targetWindow }
): void => {
  const fileName = `${filename}.${ext}`
  const mimeType = MIME_BY_EXT[ext] ?? 'application/octet-stream'

  if (targetWindow && !targetWindow.closed) {
    showBlobInWindow(targetWindow, base64ToBlob(base64FileData, mimeType))

    return
  }

  if (isIosSafari()) {
    downloadBlob(base64ToBlob(base64FileData, 'application/octet-stream'), fileName)

    return
  }

  const source = `data:application/octet-stream;base64,${base64FileData}`
  const downloadLink = document.createElement('a')

  downloadLink.href = source
  downloadLink.download = fileName
  document.body.appendChild(downloadLink)
  downloadLink.click()
  downloadLink.remove()
}
